import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import {
  Action,
  Clients,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import Config from 'react-native-config';
import { DFDHeaders } from 'serverTypes/authentication';
import DeviceInfoServices from 'services/DeviceInfoServices';
import FileServices from 'services/FileServices';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { apiKeySelector } from 'store/appSettings/selectors';
import { dfdAccessTokenSelector, fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { getBillingRequestStateSelector } from 'store/billing/selectors';
import { RootDispatch, RootState } from 'store/types';

const PREFIX = 'BILLING';
export const DETAIL_DOWNLOAD = `${PREFIX}.DETAIL_DOWNLOAD`;
export const DETAIL_DOWNLOAD_SUCCESS = `${PREFIX}.DETAIL_DOWNLOAD_SUCCESS`;
export const DETAIL_DOWNLOAD_FAIL = `${PREFIX}.DETAIL_DOWNLOAD_FAIL`;
export const DETAIL_SHARE = `${PREFIX}.DETAIL_SHARE`;
export const DETAIL_SHARE_SUCCESS = `${PREFIX}.DETAIL_SHARE_SUCCESS`;
export const DETAIL_SHARE_FAIL = `${PREFIX}.DETAIL_SHARE_FAIL`;
export const GET_PAYMENT_DETAIL = `${PREFIX}.GET_PAYMENT_DETAIL`;
export const GET_PAYMENT_LIST = `${PREFIX}.GET_PAYMENT_LIST`;
export const GET_COLLECTIONS_SUMMARY = `${PREFIX}.GET_COLLECTIONS_SUMMARY`;
export const GET_ALL_COLLECTIONS_SUMMARY = `${PREFIX}.GET_ALL_COLLECTIONS_SUMMARY`;
export const GET_ENCOUNTER_DETAIL = `${PREFIX}.GET_ENCOUNTER_DETAIL`;
export const GET_PAYMENT_PLAN_SUMMARY = `${PREFIX}.GET_PAYMENT_PLAN_SUMMARY`;
export const GET_PAYMENTS_SUMMARY = `${PREFIX}.GET_PAYMENTS_SUMMARY`;
export const GET_BILLING_SUMMARY = `${PREFIX}.GET_BILLING_SUMMARY`;
export const GET_BILLS = `${PREFIX}.GET_BILLS`;
export const GET_HSA_SUMMARY = `${PREFIX}.GET_HSA_SUMMARY`;
export const GET_FINANCIAL_ASSISTANCE_SUMMARY = `${PREFIX}.GET_FINANCIAL_ASSISTANCE_SUMMARY`;
export const GET_FINANCIAL_ASSISTANCE_APPLICATIONS = `${PREFIX}.GET_FINANCIAL_ASSISTANCE_APPLICATIONS`;
export const GET_FINANCIAL_ASSISTANCE_APPLICATION = `${PREFIX}.GET_FINANCIAL_ASSISTANCE_APPLICATION`;
export const GET_BILLING_VISIT_SUMMARY_DETAILS = `${PREFIX}.GET_BILLING_VISIT_SUMMARY_DETAILS`;

export const downloadBillingDetail = (encounterId: string) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  dispatch({ type: DETAIL_DOWNLOAD });
  try {
    const currentState = getState();
    const patientId = fhirCurrentUserPatientId(currentState);
    const accessToken = dfdAccessTokenSelector(currentState);
    const apikey = apiKeySelector(currentState);
    const consumerId = currentAccountConsumerIdSelector(currentState);
    const deviceId = DeviceInfoServices.getDeviceId();

    let url = `${Config.BILLING_BASE_URL}/rest/system/encounters/${encounterId}/download`;
    if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
      url = `${getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.BILLS,
        DispatcherEndpoint.ID_DOWNLOAD,
        true
      )}`;
    }

    const response = await FileServices.download(`billing-detail_${encounterId}.pdf`, url, {
      Accept: 'application/pdf',
      Authorization: `Bearer ${accessToken}`,
      apikey,
      [DFDHeaders.resourceId]: encounterId,
      [DFDHeaders.mhpAdapter]: 'utah',
      'x-device-id': deviceId,
      'x-dfd-consumer-id': consumerId,
      'x-patient-id': patientId
    });

    return dispatch({ type: DETAIL_DOWNLOAD_SUCCESS, payload: response });
  } catch (err) {
    return dispatch({ type: DETAIL_DOWNLOAD_FAIL, Error: err });
  }
};

export const downloadBillingDetailToStore = (encounterId: string, asPdf?: boolean): Action => {
  let url = `/rest/system/encounters/${encounterId}/download`;
  let client: Clients = 'billing';
  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    url = `${getDispatcherUrl(
      DispatcherCategoryService.BILLING,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.BILLS,
      DispatcherEndpoint.ID_DOWNLOAD
    )}`;
    client = 'dispatcher';
  }

  return {
    type: DETAIL_DOWNLOAD,
    payload: {
      client,
      request: {
        method: 'get',
        url,
        headers: {
          [DFDHeaders.resourceId]: encounterId,
          [DFDHeaders.mhpAdapter]: 'utah',
          Accept: asPdf ? 'application/pdf' : 'application/json'
        }
      }
    }
  };
};

export const shareBillingDetail = (
  payload: { id: string },
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => {
  const encounterId = payload.id;
  let client: Clients = 'billing';
  let url = `/rest/system/encounters/${encounterId}/share`;
  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    url = `${getDispatcherUrl(
      DispatcherCategoryService.BILLING,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.BILLS,
      DispatcherEndpoint.ID_TRANSMIT
    )}`;
    client = 'dispatcher';
  }

  return {
    type: DETAIL_SHARE,
    payload: {
      client,
      request: {
        method: 'post',
        url,
        headers: {
          [DFDHeaders.resourceId]: encounterId,
          [DFDHeaders.mhpAdapter]: 'utah'
        },
        data: {
          toAddress,
          message,
          useSecureEmail,
          contactPhoneNumber,
          subject
        }
      }
    }
  };
};

export const getPaymentDetail = (receiptId: any) => (dispatch: RootDispatch) => {
  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    return dispatch({
      type: GET_PAYMENT_DETAIL,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: `${getDispatcherUrl(
            DispatcherCategoryService.BILLING,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.PAYMENTS,
            DispatcherEndpoint.ID
          )}`,
          headers: {
            [DFDHeaders.resourceId]: receiptId,
            [DFDHeaders.mhpAdapter]: 'utah'
          }
        }
      }
    });
  }
  return dispatch({
    type: GET_PAYMENT_DETAIL,
    payload: {
      client: 'billing',
      request: {
        method: 'get',
        url: `/rest/system/payments/${receiptId}`
      }
    }
  });
};

export const PAYMENT_DOWNLOAD = `${PREFIX}.PAYMENT_DOWNLOAD`;
export const PAYMENT_DOWNLOAD_SUCCESS = `${PREFIX}.PAYMENT_DOWNLOAD_SUCCESS`;
export const PAYMENT_DOWNLOAD_FAIL = `${PREFIX}.PAYMENT_DOWNLOAD_FAIL`;
export const PAYMENT_SHARE = `${PREFIX}.PAYMENT_SHARE`;
export const PAYMENT_SHARE_SUCCESS = `${PREFIX}.PAYMENT_SHARE_SUCCESS`;
export const PAYMENT_SHARE_FAIL = `${PREFIX}.PAYMENT_SHARE_FAIL`;

export const downloadPaymentDetail = (receiptId: string) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  dispatch({ type: PAYMENT_DOWNLOAD });
  try {
    const currentState = getState();
    const deviceId = DeviceInfoServices.getDeviceId();
    const url =
      Config.BILLING_EXPANSION_ENABLED === 'enabled'
        ? `${Config.DISPATCHER_BASE_URL}${getDispatcherUrl(
            DispatcherCategoryService.BILLING,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.PAYMENTS,
            DispatcherEndpoint.ID_DOWNLOAD
          )}`
        : `${Config.BILLING_BASE_URL}/rest/system/payments/${receiptId}/download`;
    const headers = getDownloadHeadersWithCurrentAccountConsumerId(currentState, url);
    const response = await FileServices.download(
      `payment-detail_${receiptId.replace(/[^a-zA-Z0-9]/g, '')}.pdf`,
      url,
      {
        ...headers,
        Accept: 'application/pdf',
        'x-device-id': deviceId,
        [DFDHeaders.resourceId]: receiptId,
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    );

    return dispatch({ type: PAYMENT_DOWNLOAD_SUCCESS, payload: response });
  } catch (err) {
    return dispatch({ type: PAYMENT_DOWNLOAD_FAIL, error: err });
  }
};

export const downloadPaymentDetailToStore = (receiptId: string, asPdf?: boolean) => {
  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    const url = getDispatcherUrl(
      DispatcherCategoryService.BILLING,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.PAYMENTS,
      DispatcherEndpoint.ID_DOWNLOAD
    );
    return {
      type: PAYMENT_DOWNLOAD,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url,
          headers: {
            [DFDHeaders.resourceId]: receiptId,
            [DFDHeaders.mhpAccept]: asPdf ? 'application/pdf' : 'application/json',
            [DFDHeaders.mhpAdapter]: 'utah'
          }
        }
      }
    };
  }
  return {
    type: PAYMENT_DOWNLOAD,
    payload: {
      client: 'billing',
      request: {
        method: 'get',
        url: `/rest/system/payments/${receiptId}/download`,
        headers: {
          Accept: asPdf ? 'application/pdf' : 'application/json'
        }
      }
    }
  };
};

export const sharePaymentDetail = (
  payload: { id: string },
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
): Action => {
  const receiptId = payload.id;
  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    return {
      type: PAYMENT_SHARE,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'post',
          url: getDispatcherUrl(
            DispatcherCategoryService.BILLING,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.PAYMENTS,
            DispatcherEndpoint.ID_TRANSMIT
          ),
          headers: {
            [DFDHeaders.resourceId]: receiptId,
            [DFDHeaders.mhpAccept]: 'application/pdf',
            [DFDHeaders.mhpAdapter]: 'utah'
          },
          data: {
            toAddress,
            message,
            useSecureEmail,
            contactPhoneNumber,
            subject
          }
        }
      }
    };
  }
  return {
    type: PAYMENT_SHARE,
    payload: {
      client: 'billing',
      request: {
        method: 'post',
        url: `/rest/system/payments/${receiptId}/share`,
        data: {
          toAddress,
          message,
          useSecureEmail,
          contactPhoneNumber,
          subject
        }
      }
    }
  };
};

export const getEncounterDetail = (encounterId: string) => {
  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    return {
      type: GET_ENCOUNTER_DETAIL,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: `${getDispatcherUrl(
            DispatcherCategoryService.BILLING,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.BILLS,
            DispatcherEndpoint.ID
          )}`,
          headers: {
            [DFDHeaders.resourceId]: encounterId,
            [DFDHeaders.mhpAdapter]: 'utah'
          }
        }
      }
    };
  }

  return {
    type: GET_ENCOUNTER_DETAIL,
    payload: {
      client: 'billing',
      request: {
        method: 'get',
        url: `/rest/system/encounters/${encounterId}`
      }
    }
  };
};

export const getBills = () => ({
  type: GET_BILLS,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      url: `${getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.BILLS
      )}`,
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getCollectionsDetails = (limit = 20) => ({
  type: GET_ALL_COLLECTIONS_SUMMARY,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      url: `${getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.BILLS,
        DispatcherEndpoint.COLLECTIONS_DETAILS
      )}?limit=${limit}&offset=0`,
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getBillingSummary = () => {
  return {
    type: GET_BILLING_SUMMARY,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        url: `${getDispatcherUrl(
          DispatcherCategoryService.BILLING,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.BILLS,
          DispatcherEndpoint.SUMMARY
        )}`,
        headers: {
          [DFDHeaders.mhpAdapter]: 'utah'
        }
      }
    }
  };
};

export const getPaymentsSummary = (useRequestState = true, limit = 3) => ({
  type: GET_PAYMENTS_SUMMARY,
  payload: {
    useRequestState,
    requestStateSelector: getBillingRequestStateSelector('paymentsSummary'),
    client: 'dispatcher',
    request: {
      method: 'get',
      url: `${getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.PAYMENTS,
        DispatcherEndpoint.SUMMARY
      )}?limit=${limit}&offset=0`,
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getPayments = (
  limit: Number | undefined = undefined,
  useRequestState = true,
  offset = 0
) => ({
  type: GET_PAYMENT_LIST,
  payload: {
    useRequestState,
    requestStateSelector: getBillingRequestStateSelector('paymentsList'),
    client: 'dispatcher',
    request: {
      method: 'get',
      url: `${getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.PAYMENTS
      )}${limit === undefined ? '' : `?limit=${limit}&offset=${offset}`}`,
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getHsaSummary = (subscriberId?: string, useRequestState = true) => ({
  type: GET_HSA_SUMMARY,
  payload: {
    useRequestState,
    requestStateSelector: getBillingRequestStateSelector('hsaSummary'),
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.HSA,
        DispatcherEndpoint.SUMMARY
      ),
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah',
        [DFDHeaders.resourceId]: subscriberId
      }
    }
  }
});

export const getPaymentPlanSummary = (useRequestState = true) => ({
  type: GET_PAYMENT_PLAN_SUMMARY,
  payload: {
    useRequestState,
    requestStateSelector: getBillingRequestStateSelector('paymentPlanSummary'),
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.PAYMENT_PLAN,
        DispatcherEndpoint.SUMMARY
      ),
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getFinancialAssistanceSummary = (useRequestState = true) => ({
  type: GET_FINANCIAL_ASSISTANCE_SUMMARY,
  payload: {
    useRequestState,
    requestStateSelector: getBillingRequestStateSelector('financialAssistanceSummary'),
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.FINANCIAL_ASSISTANCE,
        DispatcherEndpoint.SUMMARY
      ),
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getFinancialAssistanceApplications = (useRequestState = true) => ({
  type: GET_FINANCIAL_ASSISTANCE_APPLICATIONS,
  payload: {
    useRequestState,
    requestStateSelector: getBillingRequestStateSelector('financialAssistanceApplications'),
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.FINANCIAL_ASSISTANCE,
        DispatcherEndpoint.APPLICATIONS
      ),
      headers: {
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getFinancialAssistanceApplicationById = (applicationId: string) => ({
  type: GET_FINANCIAL_ASSISTANCE_APPLICATION,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.FINANCIAL_ASSISTANCE,
        `${DispatcherEndpoint.APPLICATIONS}${DispatcherEndpoint.ID}`
      ),
      headers: {
        [DFDHeaders.resourceId]: applicationId,
        [DFDHeaders.mhpAdapter]: 'utah'
      }
    }
  }
});

export const getBillingVisitSummaryDetails = (accountNumber: string) => {
  return {
    type: GET_BILLING_VISIT_SUMMARY_DETAILS,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        url: getDispatcherUrl(
          DispatcherCategoryService.BILLING,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.VISIT_SUMMARIES,
          DispatcherEndpoint.ID
        ),
        headers: {
          [DFDHeaders.resourceId]: accountNumber,
          [DFDHeaders.mhpAdapter]: 'utah'
        }
      }
    }
  };
};
